import { load } from 'js-yaml';

import { frontmatterTable } from './Markdown.sty.js';

type MdastNode = {
  type: string;
  [key: string]: unknown;
};

function makeCell(children: MdastNode[]): MdastNode {
  return { type: 'tableCell', children };
}

function makeRow(children: MdastNode[]): MdastNode {
  return { type: 'tableRow', children };
}

function makeText(value: string): MdastNode {
  return { type: 'text', value };
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

function makeRawFallback(value: string): MdastNode {
  return { type: 'paragraph', children: [makeText(value)] };
}

/**
 * Remark plugin that transforms a YAML frontmatter node into a standard MDAST
 * table node, so it renders through the same `tableMappings` pipeline as any
 * other Markdown table.
 */
export function remarkFrontmatterToTable() {
  return (tree: { children: MdastNode[] }) => {
    const node = tree.children[0];
    if (node?.type !== 'yaml' || typeof node.value !== 'string') {
      return;
    }
    const rawValue = node.value;

    let data: unknown;
    try {
      data = load(rawValue);
    } catch {
      tree.children[0] = makeRawFallback(rawValue);
      return;
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      tree.children[0] = makeRawFallback(rawValue);
      return;
    }

    const entries = Object.entries(data as Record<string, unknown>);
    if (entries.length === 0) {
      tree.children[0] = makeRawFallback(rawValue);
      return;
    }

    const rows = entries.map(([key, value]) => {
      const valueText = stringifyValue(value);
      return makeRow([
        makeCell([{ type: 'strong', children: [makeText(key)] }]),
        makeCell(valueText ? [makeText(valueText)] : []),
      ]);
    });

    tree.children[0] = {
      type: 'table',
      align: [null, null],
      data: { hProperties: { className: frontmatterTable } },
      children: [makeRow([makeCell([]), makeCell([])]), ...rows],
    };
  };
}
